/**
 * Web re-export shim for the canonical currency module in packages/shared.
 *
 * The app is NIS-only. Use formatPrice() and CURRENCY_SYMBOL.
 *
 * Legacy compat stubs (getActiveCurrency, getCurrencyPreference, etc.) are kept
 * temporarily so existing import sites compile. They always reflect ILS.
 * Will be deleted along with the currency-preference picker UI.
 */

import i18n from "./i18n";
import {
  formatStartingPrice as sharedFormatStartingPrice,
  getEffectivePricingType,
  type PricingType as SharedPricingType,
} from "@eventify/shared/utils/currency";

export {
  CURRENCY,
  CURRENCY_SYMBOL,
  CURRENCY_LOCALE,
  formatPrice,
  getPriceRange,
  getEffectivePricingType,
  getCatalogVendorEffectivePrice,
  MAX_EVENT_BUDGET_ILS,
} from "@eventify/shared/utils/currency";
export type { CurrencyCode, PricingType } from "@eventify/shared/utils/currency";

/**
 * i18n-aware wrapper around shared `formatStartingPrice` — uses translated
 * "/person" and "/hr" suffixes from the active locale.
 *
 * Pass `category` so categories with implicit per-person pricing (Venue,
 * Caterer) get the suffix even when the vendor's `pricingType` field is unset.
 */
export function formatStartingPrice(
  price: number,
  pricingType?: SharedPricingType,
  category?: string,
): string {
  const effective = getEffectivePricingType(category, pricingType);
  return sharedFormatStartingPrice(price, effective, {
    perPerson: i18n.t("common.perPerson", "/person"),
    perHour: i18n.t("common.perHour", "/hr"),
  });
}

// ──────────────────────────────────────────────────────────────────
// Legacy compat — always returns ILS / no-op. Removed in Phase 10.
// ──────────────────────────────────────────────────────────────────

/** @deprecated App is NIS-only. Always returns "ILS". */
export type CurrencyPreference = "ILS";

/** @deprecated App is NIS-only. Always returns "ILS". */
export function getCurrencyPreference(): CurrencyPreference {
  return "ILS";
}

/** @deprecated App is NIS-only — preference cannot be changed. No-op. */
export function setCurrencyPreference(_pref: CurrencyPreference): void {
  /* no-op */
}

/** @deprecated App is NIS-only. Always returns "ILS". */
export function getActiveCurrency(): "ILS" {
  return "ILS";
}

/** @deprecated No-op. Exchange-rate fetching has been removed. */
export async function initExchangeRate(): Promise<void> {
  /* no-op — kept so existing call sites compile */
}

/** @deprecated Returns 1 (no conversion happens). */
export function getExchangeRate(): number {
  return 1;
}

/**
 * One-shot localStorage cleanup of legacy keys. Safe to call repeatedly.
 * Kept for ~2 release cycles, then this function + the legacy keys can go.
 */
export function cleanupLegacyCurrencyStorage(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem("eventify-currency");
    window.localStorage.removeItem("eventify-exchange-rate");
  } catch {
    /* ignore */
  }
}
